// Design frames: wireframes/15-properties-m3.html (15.12 detail, 15.13 status sheet, 15.15 photos).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Dialog,
  Card,
  Carousel,
  CarouselItem,
  Chip,
  DateField,
  Icon,
  IconButton,
  List,
  ListItem,
  Menu,
  Notice,
  Skeleton,
  StateScreen,
  Tag,
  iconChevron,
  iconEdit,
  iconMore,
  useSnackbar,
} from '@sauda/ui/kit';
import { Screen, useChrome } from '../../app/chrome.js';
import { useAuth } from '../../auth/AuthContext.js';
import { ApiError, api } from '../../lib/api.js';
import { istDateInput } from '../../lib/money-input.js';
import { formatRupees } from '../../lib/money.js';
import { formatIstDate } from '../../lib/time.js';
import { whatsappLink } from '../../lib/share-text.js';
import type { DealSummary, PropertyView, ShareInfo, VisitView } from '../../lib/types.js';
import { PhotoManager } from './PhotoManager.js';
import { SharePanel } from './SharePanel.js';

const STATUSES = ['available', 'on_hold', 'let_out_or_sold', 'archived'] as const;
type Status = (typeof STATUSES)[number];

/** One property: its photos, the rent or price in large type, what only the team sees, sharing. */
export function PropertyScreen() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const snack = useSnackbar();
  const location = useLocation();
  const arrival = location.state as { saved?: boolean; uploadFailed?: boolean } | null;
  const [error, setError] = useState<string | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [status, setStatus] = useState<Status>('available');

  const property = useQuery({
    queryKey: ['property', id],
    queryFn: () => api<PropertyView>(`/properties/${id}`),
    enabled: auth.status === 'authenticated',
    // Photos are processed by the worker, so keep looking while any is pending.
    refetchInterval: (query) =>
      query.state.data?.photos.some((p) => p.status === 'pending') ? 2000 : false,
  });
  // What has happened on this property, from the lists this role already reads.
  const visits = useQuery({
    queryKey: ['visits', auth.agencyId],
    queryFn: () => api<VisitView[]>('/visits'),
    enabled: auth.status === 'authenticated' && auth.allowed('visits.read'),
  });
  const deals = useQuery({
    queryKey: ['deals', auth.agencyId],
    queryFn: () => api<DealSummary[]>('/deals'),
    enabled: auth.status === 'authenticated' && auth.allowed('deals.run'),
  });
  const share = useQuery({
    queryKey: ['property-share', id],
    queryFn: () => api<ShareInfo>(`/properties/${id}/share`),
    // A flat with no brokerage stated cannot be shared: the screen says so instead of asking.
    enabled:
      auth.status === 'authenticated' && property.data !== undefined && property.data.readyToShare,
    retry: false,
  });
  const stillAvailable = useMutation({
    mutationFn: () => api<PropertyView>(`/properties/${id}/still-available`, { method: 'POST' }),
    onSuccess: async () => {
      snack({ text: t('property.stillAvailableDone') });
      await reload();
    },
    onError: () => snack({ text: t('property.generic') }),
  });
  const [statusOn, setStatusOn] = useState(istDateInput());
  const reload = async () => {
    await cache.invalidateQueries({ queryKey: ['property', id] });
    await cache.invalidateQueries({ queryKey: ['property-share', id] });
    await cache.invalidateQueries({ queryKey: ['properties'] });
  };
  const changeStatus = useMutation({
    mutationFn: (next: Status) =>
      api<PropertyView>(`/properties/${id}/status`, {
        method: 'POST',
        body: next === 'available' ? { status: next } : { status: next, on: statusOn },
      }),
    onSuccess: async (_row, next) => {
      setStatusOpen(false);
      setConfirmDelete(false);
      setError(null);
      await reload();
      if (next === 'archived') void navigate('/properties', { replace: true });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status === 403
          ? t('property.notAllowed')
          : t('property.generic'),
      ),
  });

  const data = property.data;
  const shareText = share.data?.text;
  // Arriving from "Save": offer to share once, then clear the navigation state.
  useEffect(() => {
    if (arrival?.saved !== true || shareText === undefined) return;
    snack({
      text: t('property.savedShare'),
      action: {
        label: t('property.shareNow'),
        onClick: () => window.open(whatsappLink(shareText), '_blank', 'noopener'),
      },
    });
    void navigate(location.pathname, { replace: true, state: null });
  }, [arrival?.saved, shareText, snack, t, navigate, location.pathname]);

  if (property.isError)
    return (
      <Screen title={t('nav.properties')} back="/properties">
        <StateScreen kind="empty" compact title={t('property.notFound')} />
      </Screen>
    );
  if (data === undefined)
    return (
      <Screen title={t('nav.properties')} back="/properties">
        <Skeleton height={200} />
      </Screen>
    );

  const amount = data.kind === 'rent' ? data.rentPaise : data.pricePaise;
  const ready = data.photos.filter((p) => p.status === 'ready');
  const editable = data.canEdit && !chrome.readOnly;
  const mine = (visits.data ?? []).filter((v) => v.propertyId === id);
  const dealsHere = (deals.data ?? []).filter((d) => d.propertyId === id);
  const count = (status: VisitView['status']) => mine.filter((v) => v.status === status).length;
  const visitBreakdown = [
    ['new', count('requested')],
    ['upcoming', count('confirmed')],
    ['done', count('done')],
  ]
    .filter(([, n]) => (n as number) > 0)
    .map(([key, n]) => t(`property.visits_${String(key)}`, { count: n as number }))
    .join(' · ');
  const latest = dealsHere[0];
  const bhk = data.bhk === null ? null : data.bhk === 0 ? '1 RK' : String(data.bhk);
  const facts: [string, string][] = [
    [
      data.kind === 'rent' ? t('property.rentMonth') : t('property.price'),
      amount === null ? '' : formatRupees(amount),
    ],
    ...(data.depositPaise === null
      ? []
      : ([[t('property.deposit'), formatRupees(data.depositPaise)]] as [string, string][])),
    ...(bhk === null ? [] : ([[t('property.bhk'), bhk]] as [string, string][])),
    ...(data.carpetSqft === null
      ? []
      : ([[t('property.carpet'), t('property.sqft', { value: data.carpetSqft })]] as [
          string,
          string,
        ][])),
    ...(data.maintenancePaise === null
      ? []
      : ([[t('property.maintenance'), formatRupees(data.maintenancePaise)]] as [string, string][])),
    ...(data.furnishing === null
      ? []
      : ([[t('property.furnishing'), t(`property.furnishing_${data.furnishing}`)]] as [
          string,
          string,
        ][])),
    ...(data.society === null
      ? []
      : ([[t('property.society'), data.society]] as [string, string][])),
    ...(data.locality === null
      ? []
      : ([[t('property.area'), data.locality]] as [string, string][])),
  ];
  const menu = editable ? (
    <>
      <IconButton
        label={t('property.edit')}
        icon={iconEdit}
        onClick={() => void navigate(`/properties/${id}/edit`)}
      />
      <Menu
        label={t('property.moreActions')}
        items={[
          {
            key: 'status',
            label: t('property.changeStatus'),
            onSelect: () => {
              setStatus(data.status);
              setStatusOpen(true);
            },
          },
          ...(data.status === 'archived'
            ? []
            : [
                {
                  key: 'delete',
                  label: t('property.delete'),
                  onSelect: () => setConfirmDelete(true),
                },
              ]),
        ]}
        trigger={(props) => (
          <IconButton {...props} label={t('property.moreActions')} icon={iconMore} />
        )}
      />
    </>
  ) : undefined;

  return (
    <Screen title={data.title} back="/properties" actions={menu}>
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      {arrival?.uploadFailed === true ? <Notice tone="error">{t('photos.failed')}</Notice> : null}
      {!data.canEdit ? <Notice tone="info">{t('property.readOnly')}</Notice> : null}
      {ready.length > 0 ? (
        <Carousel
          label={t('photos.title')}
          previousLabel={t('app.previous')}
          nextLabel={t('app.next')}
          countLabel={(n, total) => t('photos.count', { n, total })}
        >
          {ready.map((p) => (
            <CarouselItem key={p.id}>
              <img
                src={p.fullUrl}
                alt={t('photos.alt', { n: p.position })}
                width={480}
                height={360}
              />
            </CarouselItem>
          ))}
        </Carousel>
      ) : null}
      <div className="deal-client__tags">
        <Tag>{t(`property.status_${data.status}`)}</Tag>
        <Tag>{t(data.kind === 'rent' ? 'property.kindRent' : 'property.kindSale')}</Tag>
      </div>
      <dl className="facts-grid">
        {facts.map(([name, value]) => (
          <div key={name}>
            <dt className="k-t-label-sm k-muted">{name}</dt>
            <dd className="k-t-title-sm">{value}</dd>
          </div>
        ))}
      </dl>
      {visits.isSuccess || deals.isSuccess ? (
        <section aria-label={t('property.activity')} className="today-section">
          <h2 className="k-t-title-sm">{t('property.activity')}</h2>
          <List label={t('property.activity')}>
            {auth.allowed('visits.read') ? (
              <ListItem
                title={t('property.visitsCount', { count: mine.length })}
                supporting={mine.length === 0 ? t('property.noVisits') : visitBreakdown}
                trailing={<Icon icon={iconChevron} />}
                onClick={() => void navigate('/visits')}
              />
            ) : null}
            {auth.allowed('deals.run') ? (
              <ListItem
                title={t('property.dealsCount', { count: dealsHere.length })}
                supporting={
                  latest === undefined
                    ? t('property.noDeals')
                    : latest.state === 'token_recorded' && latest.memoSigners > 0
                      ? t('deal.tagMemoOut')
                      : t(`deals.state_${latest.state}`)
                }
                trailing={<Icon icon={iconChevron} />}
                onClick={() =>
                  void navigate(
                    dealsHere.length === 1 && latest !== undefined
                      ? `/deals/${latest.id}`
                      : '/deals',
                  )
                }
              />
            ) : null}
          </List>
        </section>
      ) : null}
      {data.ownerName === null && data.ownerPhone === null ? null : (
        <Card>
          <h2 className="k-t-title-sm">{t('property.teamOnly')}</h2>
          <div className="form-pair">
            <div>
              <p className="k-t-label k-muted">{t('property.ownerName')}</p>
              <p className="k-t-body">{data.ownerName}</p>
            </div>
            <div>
              <p className="k-t-label k-muted">{t('property.ownerPhone')}</p>
              <p className="k-t-body">{data.ownerPhone}</p>
            </div>
          </div>
        </Card>
      )}
      {data.status === 'available' ? (
        <Card>
          <p className="k-t-body" data-testid="last-confirmed">
            {data.lastConfirmedAt === null
              ? t('property.neverConfirmed')
              : t('property.lastConfirmed', { date: formatIstDate(data.lastConfirmedAt) })}
          </p>
          {editable ? (
            <Button
              variant="tonal"
              loading={stillAvailable.isPending}
              onClick={() => stillAvailable.mutate()}
            >
              {t('property.stillAvailable')}
            </Button>
          ) : null}
        </Card>
      ) : null}
      {data.status === 'available' && !data.readyToShare ? (
        <Notice
          tone="warning"
          action={
            editable ? (
              <Button variant="text" onClick={() => void navigate(`/properties/${id}/edit`)}>
                {t('property.addBrokerage')}
              </Button>
            ) : undefined
          }
        >
          {t('property.brokerageNeeded')}
        </Notice>
      ) : null}
      {data.status === 'available' && share.data !== undefined ? (
        <SharePanel share={share.data} />
      ) : null}
      {editable ? (
        <Card>
          <PhotoManager propertyId={id} photos={data.photos} onChanged={() => void reload()} />
        </Card>
      ) : null}
      <BottomSheet
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        label={t('property.changeStatus')}
        title={t('property.changeStatus')}
        closeLabel={t('app.close')}
        actions={
          <Button
            size="lg"
            block
            loading={changeStatus.isPending}
            disabled={status === data.status}
            onClick={() => changeStatus.mutate(status)}
          >
            {t('property.saveStatus')}
          </Button>
        }
      >
        <div className="chips-row" role="group" aria-label={t('property.statusLabel')}>
          {STATUSES.map((s) => (
            <Chip key={s} selected={status === s} onClick={() => setStatus(s)}>
              {t(`property.status_${s}`)}
            </Chip>
          ))}
        </div>
        {status === 'on_hold' || status === 'let_out_or_sold' ? (
          <DateField label={t('property.statusOn')} value={statusOn} onChange={setStatusOn} />
        ) : null}
      </BottomSheet>
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t('property.deleteTitle')}
        destructive
        actions={
          <>
            <Button variant="text" onClick={() => setConfirmDelete(false)}>
              {t('app.cancel')}
            </Button>
            <Button
              destructive
              loading={changeStatus.isPending}
              onClick={() => changeStatus.mutate('archived')}
            >
              {t('property.delete')}
            </Button>
          </>
        }
      >
        {t('property.deleteBody')}
      </Dialog>
    </Screen>
  );
}
